import Image from "next/image";
import { useRouter } from "next/router";
import { formatUSD } from "@/utils";
import { useAppSelector } from "@/store/hooks";
import { RootState } from "@/store";
import { useTranslation } from "next-i18next";
import { IAwardInfo, IUserStats } from "@/props/IDonation";
import { useMemo, useState, useEffect } from "react";

const SimpleLoading = () => (
  <div className="bg-gray-50/80 absolute inset-0 z-20 flex items-center justify-center backdrop-blur-sm transition-all duration-300">
    <div
      className="h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
      role="status"
    >
      <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
        Loading...
      </span>
    </div>
  </div>
);

const Certificate = ({
  hasName = false,
  userStats,
  fromModal = false,
  awardInfo,
}: {
  hasName?: boolean;
  userStats: IUserStats;
  fromModal?: boolean;
  awardInfo: IAwardInfo;
}) => {
  const router = useRouter();
  const { t } = useTranslation();
  const { isMobile } = useAppSelector((state: RootState) => state.app);
  const currentLocale: string = router.locale?.toUpperCase() || "EN";
  const backgrounds = {
    SC: hasName ? "/images/csr/has_name_sc.webp" : "/images/csr/no_name_sc.webp",
    TC: hasName ? "/images/csr/has_name_tc.webp" : "/images/csr/no_name_tc.webp",
  };

  const certificateBG = backgrounds[currentLocale] ?? backgrounds.SC;
  const modalScale = fromModal ? (isMobile ? 0.86 : 0.64) : 1;
  const bottomFontSize = isMobile ? "8px" : `${14 * (fromModal ? 0.86 : 1)}px`;

  const userInfo = useAppSelector((state) => state.user.userInfo);

  /** 所有 8 种布局情况统一放在 Map 中 */
  const layoutConfig = useMemo(() => {
    const key = `${hasName}-${isMobile}-${fromModal}`;

    const MAP: Record<
      string,
      {
        topAmount: number;
        topCertNo: number;
        topName?: number;
      }
    > = {
      // hasName  isMobile   fromModal
      "true-true-true": { topAmount: 44.5, topCertNo: 66, topName: 29.5 },
      "true-false-true": { topAmount: 43, topCertNo: 65, topName: 30.5 },

      "true-true-false": { topAmount: 46, topCertNo: 62, topName: 31 },
      "true-false-false": { topAmount: 44, topCertNo: 62, topName: 31 },

      "false-true-true": { topAmount: 38.5, topCertNo: 60 },
      "false-true-false": { topAmount: 40.5, topCertNo: 60 },

      "false-false-true": { topAmount: 37, topCertNo: 60 },
      "false-false-false": { topAmount: 42, topCertNo: 60 },
    };

    return MAP[key] || { topAmount: 40, topCertNo: 60, topName: 30 };
  }, [hasName, isMobile, fromModal]);

  const [isImgLoaded, setIsImgLoaded] = useState(false);

  useEffect(() => {
    setIsImgLoaded(false);
  }, [certificateBG]);

  return (
    <div
      className={`relative mx-auto overflow-hidden ${isMobile ? "px-4" : ""}`}
      style={{
        aspectRatio: isMobile ? "343/446" : "751/977",
        ...(isMobile && {
          width: fromModal ? `${343 * modalScale}px` : "100%",
          maxWidth: fromModal ? "100%" : "calc(100vw - 2rem)",
        }),
        ...(!isMobile && {
          width: `${751 * modalScale}px`,
        }),
        transition: "all 0.25s ease",
      }}
    >
      {!isImgLoaded && <SimpleLoading />}
      <Image
        src={certificateBG}
        alt="Certificate Background"
        fill
        sizes="100%"
        className="object-cover"
        onLoad={() => setIsImgLoaded(true)}
      />

      <div className="absolute inset-0 opacity-100">
        {hasName && (
          <div
            className={`absolute left-1/2 w-full -translate-x-1/2 text-center text-[#000] ${
              isMobile ? "text-base" : "text-2xl"
            }`}
            style={{
              top: `${layoutConfig.topName}%`,
              transition: "top 0.25s ease",
            }}
          >
            {userInfo?.localName}
          </div>
        )}

        <div
          className="absolute left-1/2 -translate-x-1/2 text-center font-bold"
          style={{
            top: `${layoutConfig.topAmount}%`,
            fontSize: isMobile ? "1.5rem" : "3rem",
            transition: "top 0.25s ease",
          }}
        >
          {formatUSD(userStats?.totalDonationAmountUsd)} USD
        </div>

        <div
          className={`absolute left-1/2 w-full -translate-x-1/2 text-center text-[#999999] ${
            isMobile ? "text-[10px]" : "text-base"
          }`}
          style={{
            top: `${layoutConfig.topCertNo}%`,
            transition: "top 0.25s ease",
          }}
        >
          {t("csr.certificate_number")}: {awardInfo?.certificateNo}
        </div>

        <div className="absolute" style={{ bottom: "12%", left: "6%", fontSize: bottomFontSize }}>
          {awardInfo?.eventName}
        </div>

        <div className="absolute" style={{ bottom: "12%", right: "9%", fontSize: bottomFontSize }}>
          {awardInfo?.certificateDate}
        </div>
      </div>
    </div>
  );
};

export default Certificate;
